import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, Button, Chip, Stack, Divider, Autocomplete, TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import Header from "../../components/Header";
import { getMyLiquidaciones } from "../../services/liquidacionService";
import { mapClosureToLiquidacion } from "../../utils/liquidaciones";
import { getDriversByBoss } from "../../services/driverService";
import { getStoredUserType } from "../../services/authService";

/* ===== Paletas (idénticas al resto) ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

const readStoredUser = () => {
  try {
    const raw = localStorage.getItem("userData");
    return raw ? JSON.parse(raw) : {};
  } catch (err) {
    console.warn("No se pudo leer userData almacenado", err?.message);
    return {};
  }
};

/* ===== Helpers UI reutilizables ===== */
const LabelValue = ({ label, value, palette }) => (
  <Box
    sx={{
      border: `1px solid ${palette.cardBorder}`,
      borderRadius: "10px",
      p: 1.5,
      height: "100%",
    }}
  >
    <Typography variant="caption" sx={{ color: palette.label, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </Typography>
    <Typography sx={{ color: palette.text, fontWeight: 700, mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
);

const LiquidacionCard = ({ item, palette, onDetails }) => {
  const fact = Number(item["facturacion-liquidacion"] || 0);
  const totalEmpresa = Number(item["total-empresa-liquidacion"] || 0);
  const km = Number(item["km-liquidacion"] || 0);

  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 2.5,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2, 6, 23, 0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
      }}
    >
      {/* Cabecera */}
      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
        <Typography variant="h6" sx={{ color: palette.title, fontWeight: 700 }}>
          Licencia {item.licencia || "-"}
        </Typography>
        <Chip
          label={item.conductor || "Sin conductor"}
          size="small"
          sx={{
            bgcolor: "transparent",
            border: `1px solid ${palette.cardBorder}`,
            color: palette.text,
            fontWeight: 600,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Box
  sx={{
    border: `2px solid ${palette.accent}`,
    borderRadius: "10px",
    px: 1.8,
    py: 0.8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(56, 189, 248, 0.08)", // leve brillo azul
  }}
>
  <Typography
    sx={{
      color: palette.text,
      fontWeight: 800,
      fontSize: "1rem", // más grande que antes (~16px)
      letterSpacing: 0.3,
    }}
  >
    Total empresa:{" "}
    <Box component="span" sx={{ color: palette.accent, fontWeight: 900 }}>
      €{totalEmpresa.toFixed(2)}
    </Box>
  </Typography>
</Box>
      </Stack>

      <Divider sx={{ my: 2, borderColor: palette.cardBorder }} />

      {/* KPIs */}
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6} md={3}>
          <LabelValue label="Facturación" value={`€${fact.toFixed(2)}`} palette={palette} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelValue
            label="Tarjetas"
            value={`€${Number(item["tarjetas-liquidacion"] || 0).toFixed(2)}`}
            palette={palette}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelValue
            label="Gasolina"
            value={`€${Number(item["gasolina-liquidacion"] || 0).toFixed(2)}`}
            palette={palette}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LabelValue label="Km Total" value={`${km} km`} palette={palette} />
        </Grid>
      </Grid>

      {/* CTA */}
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          onClick={onDetails}
          sx={{
            backgroundColor: palette.btn,
            color: "white",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: palette.btnHover },
          }}
        >
          Ver detalles
        </Button>
      </Stack>
    </Box>
  );
};

const BalanceSheets = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const palette = useMemo(() => (isDark ? PALETTE_DARK : PALETTE_LIGHT), [isDark]);
  const navigate = useNavigate();
  const storedUser = useMemo(readStoredUser, []);
  const userType = storedUser?.type || getStoredUserType();
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverInput, setDriverInput] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadDrivers = async () => {
      try {
        if (userType === "Boss") {
          const ds = await getDriversByBoss();
          setDrivers(ds || []);
        } else if (storedUser?.driverId) {
          const self = { _id: storedUser.driverId, name: storedUser.driverName || "Tu conductor" };
          setDrivers([self]);
          setSelectedDriver(self);
          setDriverInput(self.name);
        }
      } catch (e) {
        console.warn("No se pudieron cargar los conductores", e?.message);
      }
    };

    loadDrivers();
  }, [userType, storedUser]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const filters = {
          startDate: dateFrom || undefined,
          endDate: dateTo || undefined,
        };
        if (selectedDriver?._id) filters.driverId = selectedDriver._id;
        else if (driverInput.trim()) filters.conductor = driverInput.trim();

        const closures = await getMyLiquidaciones(filters);
        const mapped = (closures || []).map(mapClosureToLiquidacion).filter(Boolean);
        setLiquidaciones(mapped);
      } catch (e) {
        setError(e.message || "No se pudieron cargar las liquidaciones");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [dateFrom, dateTo, selectedDriver, driverInput]);

  return (
    <Box m="20px" sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Header title="Liquidaciones" subtitle="Listado de liquidaciones registradas" />
      {error && (
        <Typography color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {/* Filtros */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <Autocomplete
          size="small"
          options={drivers}
          freeSolo
          getOptionLabel={(o) => o?.name || o?.fullName || o?.username || o || ""}
          value={selectedDriver}
          inputValue={driverInput}
          onInputChange={(_, v) => setDriverInput(v || "")}
          onChange={(_, v) => {
            setSelectedDriver(v || null);
            setDriverInput(v?.name || v?.fullName || v?.username || "");
          }}
          renderInput={(params) => (
            <TextField {...params} label="Filtrar por conductor" placeholder="Selecciona o escribe un conductor" />
          )}
          sx={{ minWidth: 260 }}
          clearOnEscape
        />
        <TextField
          label="Desde"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            "& .MuiInputBase-input": { color: palette.text },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
            "& input::-webkit-calendar-picker-indicator": {
              filter: isDark ? "invert(1)" : "none",
            },
          }}
          inputProps={{
            onClick: (e) => {
              try {
                e.target.showPicker?.();
              } catch {
                /* ignora navegadores que no lo permitan */
              }
            },
          }}
        />
        <TextField
          label="Hasta"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            minWidth: 160,
            "& .MuiInputBase-input": { color: palette.text },
            "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: palette.accent },
            "& .MuiInputLabel-root.Mui-focused": { color: palette.text },
            "& input::-webkit-calendar-picker-indicator": {
              filter: isDark ? "invert(1)" : "none",
            },
          }}
          inputProps={{
            onClick: (e) => {
              try {
                e.target.showPicker?.();
              } catch {
                /* ignora navegadores que no lo permitan */
              }
            },
          }}
        />
      </Stack>

      {/* Lista de cards */}
      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 2,
        }}
      >
        {loading && <Typography sx={{ color: palette.label }}>Cargando liquidaciones...</Typography>}
        {!loading && !liquidaciones.length && (
          <Typography sx={{ color: palette.label }}>No hay liquidaciones disponibles.</Typography>
        )}
        {!loading &&
          liquidaciones.map((row) => (
            <LiquidacionCard
              key={row.id}
              item={row}
              palette={palette}
              onDetails={() => navigate(`/liquidaciones/${row.id}`, { state: { liquidacion: row } })}
            />
          ))}
      </Box>
    </Box>
  );
};

export default BalanceSheets;
